import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";

/**
 * VoiceInput — herbruikbare knop voor spraak → tekst via OpenAI Whisper.
 *
 * Props:
 *   onTranscript(text: string)  — callback met de getranscribeerde tekst
 *   language                    — ISO taal (default: "nl")
 *   prompt                      — optionele context-prompt (bv. terminologie)
 *   size                        — "sm" | "md" (default sm)
 *   variant                     — "ghost" | "solid" (default ghost)
 *   className                   — extra classes
 *   testId                      — data-testid
 *   append                      — als true → transcriptie toevoegen aan bestaande tekst (met spatie),
 *                                 anders vervangen (default: true)
 *   getCurrentText              — () => string : haalt huidige input-waarde op (voor append)
 *
 * Vereist: browser MediaRecorder API (Chrome/Firefox/Safari 14+)
 */
export default function VoiceInput({
  onTranscript,
  language = "nl",
  prompt = "",
  size = "sm",
  variant = "ghost",
  className = "",
  testId = "voice-input",
  append = true,
  getCurrentText = null,
}) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [supported, setSupported] = useState(true);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.MediaRecorder || !navigator.mediaDevices?.getUserMedia) {
      setSupported(false);
    }
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (!supported) {
      toast.error("Audio-opname niet ondersteund in deze browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      // Probeer opus/webm; fallback op default
      let mime = "";
      const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
      for (const m of candidates) {
        if (window.MediaRecorder.isTypeSupported(m)) { mime = m; break; }
      }
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        chunksRef.current = [];
        if (blob.size < 1000) {
          toast.warning("Opname te kort — probeer opnieuw");
          return;
        }
        await transcribe(blob, mr.mimeType || "audio/webm");
      };
      mr.start();
      setRecording(true);
    } catch (err) {
      toast.error("Microfoon-toegang geweigerd of niet beschikbaar");
    }
  };

  const stopRecording = () => {
    try {
      mediaRecorderRef.current?.stop();
    } catch {}
    setRecording(false);
  };

  const transcribe = async (blob, mime) => {
    setBusy(true);
    try {
      const ext = mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : "webm";
      const fd = new FormData();
      fd.append("file", blob, `spraak.${ext}`);
      fd.append("language", language);
      if (prompt) fd.append("prompt", prompt);

      const { data } = await api.post("/ai/voice/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (data?.text || "").trim();
      if (!text) {
        toast.warning("Geen tekst herkend");
        return;
      }
      if (append && getCurrentText) {
        const cur = getCurrentText() || "";
        const sep = cur && !cur.endsWith(" ") && !cur.endsWith("\n") ? " " : "";
        onTranscript?.(cur + sep + text);
      } else {
        onTranscript?.(text);
      }
      toast.success(`Ingesproken: ${text.slice(0, 50)}${text.length > 50 ? "…" : ""}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Transcriptie mislukt");
    }
    setBusy(false);
  };

  const handleClick = () => {
    if (busy) return;
    if (recording) stopRecording();
    else startRecording();
  };

  if (!supported) return null;

  const sizeClasses = size === "sm" ? "h-9 w-9" : "h-10 w-10";
  const baseClasses =
    variant === "solid"
      ? "bg-[hsl(var(--brand-blue))] text-white hover:bg-[hsl(var(--brand-blue))]/90"
      : "bg-slate-100 hover:bg-slate-200 text-slate-700";
  const recClass = recording ? "bg-red-500 text-white hover:bg-red-600 animate-pulse" : baseClasses;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      title={recording ? "Opname stoppen" : "Inspreken (spraak-naar-tekst)"}
      aria-label={recording ? "Opname stoppen" : "Inspreken"}
      className={`inline-flex items-center justify-center rounded-md ${sizeClasses} ${recClass} disabled:opacity-50 transition-colors ${className}`}
      data-testid={testId}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
    </button>
  );
}
